import React, { Component } from 'react';
import styled from 'styled-components';
import {
  C, Panel, PanelHeader, PanelTitle, CountPill,
  TableWrap, Table, Empty, Badge, SearchInput,
} from '../chrome';
import RecordingPlayer from './RecordingPlayer';
import Pagination, { sliceForPage } from './Pagination';
import {
  formatTimestamp, formatTime, formatDuration, reasonLabel,
  toCsv, downloadCsv,
} from '../utils';

const PAGE_SIZE = 10;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 1.6rem;
  border-bottom: 1px solid ${C.border};
  background: ${C.surface};
`;

const Tab = styled.button`
  position: relative;
  background: transparent;
  border: none;
  color: ${(p) => (p.active ? C.textPrimary : C.textMuted)};
  font-size: 1.25rem;
  font-weight: 600;
  padding: 1.4rem 1.4rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  transition: color 0.15s;

  &:hover { color: ${C.textPrimary}; }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 1rem;
    right: 1rem;
    height: 2px;
    background: ${(p) => (p.active ? C.purple : 'transparent')};
    transition: background 0.15s;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.4rem;
  padding: 1.2rem 1.6rem;
  border-bottom: 1px solid ${C.border};
  background: ${C.surface};
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ToolbarBtn = styled.button`
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  color: ${C.textSecondary};
  border-radius: 0.6rem;
  padding: 0.7rem 1.1rem;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: ${C.purple};
    color: ${C.textPrimary};
    background: ${C.purpleDim};
  }
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.color || C.textMuted};
  display: inline-block;
  margin-right: 0.6rem;
`;

const TABS = [
  { key: 'pending',     label: 'Pending' },
  { key: 'inFlight',    label: 'In-flight' },
  { key: 'exhausted',   label: 'Exhausted' },
  { key: 'connected',   label: 'Connected' },
  { key: 'agents',      label: 'Agent Performance' },
];

// Naive substring search across all string-coerced row values.
const matches = (row, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return Object.values(row || {}).some((v) =>
    v != null && String(v).toLowerCase().includes(needle)
  );
};

class DeepDiveTabs extends Component {
  state = {
    active: 'pending',
    query:  '',
    page:   1,
  };

  setActive = (key) => this.setState({ active: key, query: '', page: 1 });
  setQuery  = (e)   => this.setState({ query: e.target.value, page: 1 });
  setPage   = (p)   => this.setState({ page: p });

  countFor = (key) => {
    const { pending, inFlight, exhausted, connected, agentPerformance } = this.props;
    switch (key) {
      case 'pending':   return (pending || []).length;
      case 'inFlight':  return (inFlight || []).length;
      case 'exhausted': return (exhausted || []).length;
      case 'connected': return (connected || []).length;
      case 'agents':    return (agentPerformance || []).length;
      default:          return 0;
    }
  };

  exportActive = () => {
    const { active } = this.state;
    const rows = this.filteredRowsFor(active);
    if (!rows.length) return;
    const csv = toCsv(rows);
    downloadCsv(`auto-dial-${active}-${Date.now()}.csv`, csv);
  };

  filteredRowsFor = (key) => {
    const { pending, inFlight, exhausted, connected, agentPerformance } = this.props;
    const { query } = this.state;
    const src = (() => {
      switch (key) {
        case 'pending':   return pending || [];
        case 'inFlight':  return inFlight || [];
        case 'exhausted': return exhausted || [];
        case 'connected': return connected || [];
        case 'agents':    return agentPerformance || [];
        default:          return [];
      }
    })();
    return src.filter((r) => matches(r, query));
  };

  renderPending = (rows, startIdx = 0) => {
    if (!rows.length) return <Empty>No pending cases.</Empty>;
    return (
      <Table>
        <thead>
          <tr>
            <th>#</th><th>Application</th><th>Name</th><th>Phone</th>
            <th>Attempts</th><th>Reason</th><th>Next dial at</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td>{startIdx + i + 1}</td>
              <td><strong>{r.application_no || '—'}</strong></td>
              <td>{r.name || '—'}</td>
              <td>{r.phone || '—'}</td>
              <td>{r.attempts || 0}</td>
              <td><Badge color={C.yellow}>{reasonLabel(r.reason)}</Badge></td>
              <td>{formatTimestamp(r.next_attempt_at)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderInFlight = (rows, startIdx = 0) => {
    if (!rows.length) return <Empty>Nothing currently dialing.</Empty>;
    return (
      <Table>
        <thead>
          <tr>
            <th>#</th><th>Application</th><th>Name</th><th>Phone</th>
            <th>Attempts</th><th>Started at</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td>{startIdx + i + 1}</td>
              <td><strong>{r.application_no || '—'}</strong></td>
              <td>{r.name || '—'}</td>
              <td>{r.phone || '—'}</td>
              <td>{r.attempts || 0}</td>
              <td>
                <Badge color={C.blue}>
                  <StatusDot color={C.blue} />
                  {formatTime(r.last_attempted_at)}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderExhausted = (rows, startIdx = 0) => {
    if (!rows.length) return <Empty>No exhausted cases — every customer was reached.</Empty>;
    const { onRequeue } = this.props;
    return (
      <Table>
        <thead>
          <tr>
            <th>#</th><th>Application</th><th>Name</th><th>Phone</th>
            <th>Attempts</th><th>Last reason</th><th>Last tried</th><th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td>{startIdx + i + 1}</td>
              <td><strong>{r.application_no || '—'}</strong></td>
              <td>{r.name || '—'}</td>
              <td>{r.phone || '—'}</td>
              <td>{r.attempts || 0}</td>
              <td><Badge color={C.red}>{reasonLabel(r.reason)}</Badge></td>
              <td>{formatTimestamp(r.updated_at)}</td>
              <td>
                {onRequeue && r.application_no && (
                  <ToolbarBtn onClick={() => onRequeue(r.application_no)}>
                    Re-queue
                  </ToolbarBtn>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderConnected = (rows, startIdx = 0) => {
    if (!rows.length) return <Empty>No connected calls yet today.</Empty>;
    return (
      <Table>
        <thead>
          <tr>
            <th>#</th><th>Application</th><th>Name</th><th>Agent</th>
            <th>Duration</th><th>Connected at</th><th>Recording</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id}>
              <td>{startIdx + i + 1}</td>
              <td><strong>{r.application_no || '—'}</strong></td>
              <td>{r.name || '—'}</td>
              <td>{r.doctor_name || '—'}</td>
              <td>{formatDuration(r.call_duration)}</td>
              <td>{formatTimestamp(r.created_at)}</td>
              <td><RecordingPlayer src={r.recording_url} /></td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  renderAgents = (rows, startIdx = 0) => {
    if (!rows.length) return <Empty>No agent has handled a call yet today.</Empty>;
    return (
      <Table>
        <thead>
          <tr>
            <th>#</th><th>Agent</th><th>Status</th>
            <th>Calls today</th><th>Connected</th>
            <th>Customer no-ans</th><th>Agent no-ans</th><th>Failed</th>
            <th>Avg duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const calls = Number(r.callsToday || 0);
            const connected = Number(r.connected || 0);
            const successRate = calls > 0 ? connected / calls : 0;
            const onCall = !!Number(r.onCallNow);
            return (
              <tr key={r.agent_id}>
                <td>{startIdx + i + 1}</td>
                <td><strong>{r.agent_name || `#${r.agent_id}`}</strong></td>
                <td>
                  <Badge color={onCall ? C.blue : C.textMuted}>
                    <StatusDot color={onCall ? C.blue : C.textMuted} />
                    {onCall ? 'On call' : 'Idle'}
                  </Badge>
                </td>
                <td>{calls}</td>
                <td>
                  <Badge color={successRate >= 0.7 ? C.green : (successRate >= 0.4 ? C.yellow : C.red)}>
                    {connected} ({(successRate * 100).toFixed(0)}%)
                  </Badge>
                </td>
                <td>{r.noAnswerCustomer || 0}</td>
                <td>{r.noAnswerAgent || 0}</td>
                <td>{r.failed || 0}</td>
                <td>{formatDuration(r.avgDurationSeconds)}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  renderActive = (rows, startIdx) => {
    switch (this.state.active) {
      case 'pending':   return this.renderPending(rows, startIdx);
      case 'inFlight':  return this.renderInFlight(rows, startIdx);
      case 'exhausted': return this.renderExhausted(rows, startIdx);
      case 'connected': return this.renderConnected(rows, startIdx);
      case 'agents':    return this.renderAgents(rows, startIdx);
      default:          return null;
    }
  };

  render() {
    const { active, query, page } = this.state;
    const filtered  = this.filteredRowsFor(active);
    const totalRows = filtered.length;
    const pageRows  = sliceForPage(filtered, page, PAGE_SIZE);
    const startIdx  = (Math.max(1, page) - 1) * PAGE_SIZE;

    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>Deep Dive</PanelTitle>
          <CountPill color={C.purple}>{totalRows} rows</CountPill>
        </PanelHeader>

        <TabRow>
          {TABS.map((t) => (
            <Tab
              key={t.key}
              active={active === t.key}
              onClick={() => this.setActive(t.key)}
            >
              {t.label}
              <CountPill color={active === t.key ? C.purple : null}>
                {this.countFor(t.key)}
              </CountPill>
            </Tab>
          ))}
        </TabRow>

        <Toolbar>
          <SearchInput
            placeholder="Search application, name, phone…"
            value={query}
            onChange={this.setQuery}
          />
          <ToolbarRight>
            <ToolbarBtn onClick={this.exportActive}>Export CSV</ToolbarBtn>
          </ToolbarRight>
        </Toolbar>

        <TableWrap>{this.renderActive(pageRows, startIdx)}</TableWrap>

        {totalRows > 0 && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={totalRows}
            onChange={this.setPage}
          />
        )}
      </Panel>
    );
  }
}

export default DeepDiveTabs;
